import { ChoiceList, FilterInterface, Filters, TextField } from "@shopify/polaris";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { initialFilterState } from "src/pages/products/items/components/productlist/context/FilterContext";
import { useClassification } from "src/services/provider/ClassificationProvider";

interface FilterProductWithSKUProps {
  filter: any;
  search: string | undefined;
  onSearchChange: (search: string | undefined) => void;
  onFilterChange: (filters: any) => void;
}

export function FilterProductWithSKU({ filter, onFilterChange, search, onSearchChange }: FilterProductWithSKUProps) {
  const { t } = useTranslation();
  const [queryValue, setQueryValue] = useState<string | undefined>(search);
  const { category, brand, vendor } = useClassification();
  const [state, setState] = useState(filter);

  const handleFiltersQueryChange = useCallback(
    (value: string) => {
      setQueryValue(value);
      if (value === "") {
        onSearchChange(undefined);
      }
    },
    [onSearchChange],
  );

  //--- filter change
  const handleStatusChange = useCallback(
    (value) => {
      setState({ ...state, status: value });
      onFilterChange({ ...filter, status: value });
    },
    [filter, onFilterChange, state],
  );

  const handleMarkChange = useCallback(
    (value) => {
      setState({ ...state, mark: value });
      onFilterChange({ ...filter, mark: value });
    },
    [filter, onFilterChange, state],
  );

  const handleRemoteChange = useCallback(
    (value) => {
      setState({ ...state, isRemoted: value[0] });
      onFilterChange({ ...filter, isRemoted: value[0] });
    },
    [filter, onFilterChange, state],
  );

  const handleGteStockChange = useCallback(
    (value) => {
      setState({ ...state, gteStock: value });
      onFilterChange({ ...filter, gteStock: value });
    },
    [filter, onFilterChange, state],
  );

  const handleLteStockChange = useCallback(
    (value) => {
      setState({ ...state, lteStock: value });
      onFilterChange({ ...filter, lteStock: value });
    },
    [filter, onFilterChange, state],
  );

  const handleGteDiscountChange = useCallback(
    (value) => {
      setState({ ...state, gteDiscount: value });
      onFilterChange({ ...filter, gteDiscount: value });
    },
    [filter, onFilterChange, state],
  );

  const handleLteDiscountChange = useCallback(
    (value) => {
      setState({ ...state, lteDiscount: value });
      onFilterChange({ ...filter, lteDiscount: value });
    },
    [filter, onFilterChange, state],
  );
  //--- end filter change

  //--- filter remove
  // const handleViewByRemove = useCallback(() => {
  //   setState({ ...state, viewBySku: "F" });
  //   onFilterChange({ ...filter, viewBySku: ["F"] });
  // }, [filter, onFilterChange, state]);

  const handleStatusRemove = useCallback(() => {
    setState({ ...state, status: ["ACTIVE"] });
    onFilterChange({ ...filter, status: ["ACTIVE"] });
  }, [filter, onFilterChange, state]);

  const handleMarkRemove = useCallback(() => {
    setState({ ...state, mark: [] });
    onFilterChange({ ...filter, mark: [] });
  }, [filter, onFilterChange, state]);

  const handleRemoteRemove = useCallback(() => {
    setState({ ...state, isRemoted: null });
    onFilterChange({ ...filter, isRemoted: null });
  }, [filter, onFilterChange, state]);

  const handleStockRemove = useCallback(() => {
    setState({ ...state, gteStock: undefined, lteStock: undefined });
    onFilterChange({ ...filter, gteStock: undefined, lteStock: undefined });
  }, [filter, onFilterChange, state]);

  const handleDiscountRemove = useCallback(() => {
    setState({ ...state, gteDiscount: undefined, lteStock: undefined });
    onFilterChange({ ...filter, lteDiscount: undefined, lteStock: undefined });
  }, [filter, onFilterChange, state]);

  const handleCategoryRemove = useCallback(() => {
    setState({ ...state, categories: [] });
    onFilterChange({ ...filter, categories: [] });
  }, [filter, onFilterChange, state]);

  const handleBrandRemove = useCallback(() => {
    setState({ ...state, categories: [] });
    onFilterChange({ ...filter, brand: [] });
  }, [filter, onFilterChange, state]);

  const handleVendorRemove = useCallback(() => {
    setState({ ...state, categories: [] });
    onFilterChange({ ...filter, vendor: [] });
  }, [filter, onFilterChange, state]);
  //--- end filter remove

  const handleQueryValueRemove = useCallback(() => {
    setQueryValue(undefined);
    onSearchChange(undefined);
  }, [onSearchChange]);

  const handleFiltersClearAll = useCallback(() => {
    setState(initialFilterState);
    onFilterChange(initialFilterState);
  }, [onFilterChange]);

  const filters: FilterInterface[] = [
    {
      key: "status",
      label: t('status'),
      filter: (
        <ChoiceList
          title=""
          titleHidden
          choices={[
            { label: "Active", value: "ACTIVE" },
            { label: "Inactive", value: "INACTIVE" },
          ]}
          selected={state.status}
          onChange={handleStatusChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: "mark",
      label: t('mark'),
      filter: (
        <ChoiceList
          title=""
          titleHidden
          choices={[
            { label: "Discontinue", value: "DISCONTINUE" },
            { label: "Out of Stock", value: "OUT_OF_STOCK" },
          ]}
          selected={state.mark}
          onChange={handleMarkChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: "isRemoted",
      label: t('remoted'),
      filter: (
        <ChoiceList
          choices={[
            { label: "No Filter", value: "" },
            { label: "Yes", value: "T" },
            { label: "No", value: "F" },
          ]}
          allowMultiple={false}
          title
          titleHidden
          selected={[state.isRemoted ? state.isRemoted : ""]}
          onChange={handleRemoteChange}
        />
      ),
      shortcut: true,
    },
    {
      label: t('vendor'),
      key: "vendor",
      filter: (
        <ChoiceList
          choices={
            vendor.map((x) => {
              return {
                label: x.name,
                value: x.id,
              };
            }) as any
          }
          allowMultiple
          title
          titleHidden
          selected={state.vendor}
          onChange={useCallback(
            (value) => {
              setState({ ...state, vendor: value });
              onFilterChange({ ...filter, vendor: value });
            },
            [state, onFilterChange, filter],
          )}
        />
      ),
    },
    {
      label: t('brand'),
      key: "brand",
      filter: (
        <ChoiceList
          choices={
            brand.map((x) => {
              return {
                label: x.name,
                value: x.id,
              };
            }) as any
          }
          allowMultiple
          title
          titleHidden
          selected={state.brand}
          onChange={useCallback(
            (value) => {
              setState({ ...state, brand: value });
              onFilterChange({ ...filter, brand: value });
            },
            [state, onFilterChange, filter],
          )}
        />
      ),
    },
    {
      label: t('category'),
      key: "category",
      filter: (
        <ChoiceList
          choices={
            (category
              ? category
                .filter((x) => x.parentID === 0)
                .map((x) => {
                  return {
                    label: x.name?.en + "",
                    value: x.id,
                  };
                })
              : []) as any
          }
          allowMultiple
          title
          titleHidden
          selected={state.categories}
          onChange={useCallback(
            (value) => {
              const x = value.map((v: any) => {
                const find: any = category.find((f) => f.id === v);
                return [
                  v,
                  String(find ? find.subCategory : "")
                    .split(",")
                    .map((f) => Number(f)),
                ].flat();
              });
              setState({ ...state, categories: x.flat() });
              onFilterChange({ ...filter, categories: x.flat() });
            },
            [state, onFilterChange, filter, category],
          )}
        />
      ),
      shortcut: true,
    },
    {
      key: "stock",
      label: t('stock'),
      filter: (
        <div>
          <TextField
            autoComplete="off"
            value={state.gteStock}
            onChange={handleGteStockChange}
            prefix="GTE"
            label=""
            labelHidden
            suffix="UNIT"
          />
          <br />
          <TextField
            autoComplete="off"
            value={state.lteStock}
            onChange={handleLteStockChange}
            prefix="LTE"
            label=""
            labelHidden
            suffix="UNIT"
          />
        </div>
      ),
      shortcut: true,
    },
    {
      key: "discount",
      label: t('discount'),
      filter: (
        <div>
          <TextField
            autoComplete="off"
            value={state.gteDiscount}
            onChange={handleGteDiscountChange}
            prefix="GTE"
            label=""
            labelHidden
            suffix="%"
          />
          <br />
          <TextField
            autoComplete="off"
            value={state.lteDiscount}
            onChange={handleLteDiscountChange}
            prefix="LTE"
            label=""
            labelHidden
            suffix="%"
          />
        </div>
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: any = [];

  if (state.status.length > 0) {
    appliedFilters.push({
      key: "status",
      label: `${t('status')}: ${state.status}`,
      onRemove: handleStatusRemove,
    });
  }

  if (state.mark.length > 0) {
    appliedFilters.push({
      key: "mark",
      label: `${t('mark')}: ${state.mark}`,
      onRemove: handleMarkRemove,
    });
  }

  if (state.isRemoted) {
    appliedFilters.push({
      key: "isRemoted",
      label: `${t('remoted')} ${state.isRemoted === "T" ? "YES" : "NO"}`,
      onRemove: handleRemoteRemove,
    });
  }

  if (state.categories.length > 0) {
    appliedFilters.push({
      key: "category",
      label: `${t('category')} ${category
        .filter((x) => x.parentID === 0 && (state.categories as any[]).includes(x.id))
        .map((x) => x.id)
        .join(",")}`,
      onRemove: handleCategoryRemove,
    });
  }

  if (state.brand.length > 0) {
    appliedFilters.push({
      key: "brand",
      label: `${t('brand')} ${state.brand.join(",")}`,
      onRemove: handleBrandRemove,
    });
  }

  if (state.vendor.length > 0) {
    appliedFilters.push({
      key: "verndor",
      label: `${t('vendor')} ${state.vendor.join(",")}`,
      onRemove: handleVendorRemove,
    });
  }

  if (state.gteStock && state.lteStock) {
    appliedFilters.push({
      key: "stock",
      label: `${t('stock')} GTE : ${state.gteStock} - LTE : ${state.lteStock}`,
      onRemove: handleStockRemove,
    });
  }

  if (state.gteDiscount && state.lteDiscount) {
    appliedFilters.push({
      key: "discount",
      label: `${t('discount')} GTE : ${state.gteDiscount} - LTE : ${state.lteDiscount}`,
      onRemove: handleDiscountRemove,
    });
  }

  return (
    <div onKeyDown={(e) => e.keyCode === 13 && onSearchChange(queryValue)}>
      <Filters
        queryPlaceholder={t('search_product_code')}
        queryValue={queryValue}
        filters={filters}
        appliedFilters={appliedFilters}
        onQueryChange={handleFiltersQueryChange}
        onQueryClear={handleQueryValueRemove}
        onClearAll={handleFiltersClearAll}
      />
    </div>
  );
}
